import { ChevronDown, ChevronRight } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { TableCell, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { NestedDrawerContainer } from "@/components/shared/NestedDrawerTable";
import { ProductTableCell } from "@/components/shared/ProductTableCell";
import { KeywordRowsTable } from "../KeywordRowsTable";
import { cn } from "@/lib/utils";
import { ComposedChart, Line, Bar, YAxis, ResponsiveContainer } from "recharts";
import { RankTrackerHeroProduct, RankTrackerKeyword } from "@/types";

interface ProductRowProps {
  product: RankTrackerHeroProduct;
  isPublic: boolean;
  isExpanded: boolean;
  isSelected: boolean;
  onToggleExpand: (id: number) => void;
  onToggleSelect: (id: number) => void;
  publicKeywords?: RankTrackerKeyword[];
  dateFrom?: string;
  dateTo?: string;
}

export function ProductRow({
  product,
  isPublic,
  isExpanded,
  isSelected,
  onToggleExpand,
  onToggleSelect,
  publicKeywords,
  dateFrom,
  dateTo,
}: ProductRowProps) {
  return (
    <Collapsible
      key={product.id}
      open={isExpanded}
      onOpenChange={() => onToggleExpand(product.id)}
      asChild
    >
      <>
        <CollapsibleTrigger asChild>
          <TableRow
            className={cn(
              "cursor-pointer hover:!bg-transparent [&>td]:transition-colors [&:hover>td]:!bg-surface-tint-soft [&>td]:text-xs"
            )}
          >
            {!isPublic && (
              <TableCell className="py-2 px-3 text-center" onClick={(e) => e.stopPropagation()}>
                <Checkbox
                  className="bg-white"
                  checked={isSelected}
                  onCheckedChange={() => onToggleSelect(product.id)}
                />
              </TableCell>
            )}
            <TableCell className="py-2 px-3 text-center">
              {isExpanded ? (
                <ChevronDown className="h-4 w-4 text-muted-foreground" />
              ) : (
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              )}
            </TableCell>
            <TableCell className="py-2 px-3">
              <ProductTableCell
                name={product.name}
                asin={product.asin}
                imageUrl={product.image_url}
                size="lg"
                wrapName
                asinLinkToAmazon
              />
            </TableCell>
            <TableCell className="py-2 px-3 text-center">
              <Badge variant="primary" className="font-semibold">
                {product.keywordCount}
              </Badge>
            </TableCell>
            <TableCell className="py-2 px-3 text-center">
              <span className={cn(
                "font-medium",
                (product.top10Count ?? 0) > 0 ? "text-trend-positive" : "text-muted-foreground"
              )}>
                {product.top10Count ?? 0}
              </span>
            </TableCell>
            <TableCell className="py-2 px-3 text-center">
              <span className={cn(
                "font-medium",
                (product.top50Count ?? 0) > 0 ? "text-trend-positive" : "text-muted-foreground"
              )}>
                {product.top50Count ?? 0}
              </span>
            </TableCell>
            <TableCell className="py-2 px-3 text-center font-medium">
              {product.avgRank ?? "—"}
            </TableCell>
            <TableCell className="py-2 px-3 text-center">
              {product.bestRank ? (
                <span className="text-trend-positive font-semibold">
                  #{product.bestRank}
                </span>
              ) : (
                "—"
              )}
            </TableCell>
            <TableCell className="py-2 px-3 text-center">
              {/* Read-only since AUT-369 deleted SalesCorrelationModal: the chart is the whole cell,
                  so there is nothing to stop the row's expand click for and nothing to tooltip. */}
              {(product.correlationTrend ?? []).some(d => d.avgRank != null || d.unitsSold != null || d.bsr != null) ? (
                <div className="w-24 h-8 mx-auto">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={product.correlationTrend}>
                      <YAxis yAxisId="rank" domain={['dataMin', 'dataMax']} reversed hide />
                      <YAxis yAxisId="sales" domain={['dataMin', 'dataMax']} hide />
                      <YAxis yAxisId="bsr" domain={['dataMin', 'dataMax']} dataKey="bsr" hide />
                      <Line yAxisId="rank" type="monotone" dataKey="avgRank" stroke="hsl(var(--primary))" strokeWidth={1.5} dot={(product.correlationTrend ?? []).filter(d => d.avgRank != null).length === 1} isAnimationActive={false} />
                      <Line yAxisId="bsr" type="monotone" dataKey="bsr" stroke="hsl(var(--muted-foreground))" strokeWidth={1} strokeDasharray="2 2" dot={false} />
                      <Bar yAxisId="sales" dataKey="unitsSold" fill="hsl(var(--primary) / 0.2)" radius={0} isAnimationActive={false} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <span className="text-muted-foreground">—</span>
              )}
            </TableCell>
            <TableCell className="py-2 px-3 w-[60px]"></TableCell>
          </TableRow>
        </CollapsibleTrigger>
        <CollapsibleContent asChild>
          <TableRow className="hover:bg-transparent">
            <TableCell colSpan={isPublic ? 9 : 10} className="p-0">
              <NestedDrawerContainer>
                <KeywordRowsTable
                  heroProductId={product.id}
                  asin={product.asin}
                  productName={product.name}
                  publicKeywords={publicKeywords}
                  dateFrom={dateFrom}
                  dateTo={dateTo}
                />
              </NestedDrawerContainer>
            </TableCell>
          </TableRow>
        </CollapsibleContent>
      </>
    </Collapsible>
  );
}
